import { useEffect, useState } from "react";
import type { DefinitionProblem, DefinitionReadiness } from "@keydials/api-client";
import { segmentNoun, type BuilderSelection } from "@keydials/dial-workspace";
import { builderCopy as copy } from "../copy";
import type { DraftSession, DraftSnapshot } from "../draftSession";
import { goLiveSheet, runGoLive, runningDialOf, withProblems } from "../launch";
import { LimitSheet } from "../LimitSheet";
import type { LimitSheetView } from "../limitSheetView";
import { readinessView } from "../readiness";
import { runBackToEditing, runTryItFirst } from "../testCopy";
import { authoringCalls, builderDeps } from "../useDraftSession";

interface LaunchStageProps {
  id: string;
  session: DraftSession;
  snapshot: DraftSnapshot;
  readiness: DefinitionReadiness;
  /** The design's lifecycle when it was opened. */
  lifecycle: string;
  /** The test copy the design already had when it was opened. */
  checkingInstanceId: string | null;
  /** "Fix this": back to Details with the problem's field showing, or to Build with its section or action point selected. */
  onFix: (selection: BuilderSelection) => void;
  /** Gone live: the running dial's Members settings, to invite people. */
  onLive: (instanceId: string) => void;
  onOpen: (instanceId: string) => void;
  onOpenTestCopy: (instanceId: string) => void;
  /** The test copy has been put away and the design can be edited again. */
  onBackToEditing: () => void;
  onGone: () => void;
}

type Busy = "tryItFirst" | "goLive" | "backToEditing" | null;

/**
 * Launch (KEY-560): what still stands between the dial and going live, then the two ways out. Try it first makes a test copy on
 * My Dials for the author alone; Go live makes the real one. While a test copy exists the builder can't be edited, and Back to
 * editing puts it away. A dial that's already running opens here too, with the way to it.
 */
export function LaunchStage({
  id,
  session,
  snapshot,
  readiness,
  lifecycle,
  checkingInstanceId,
  onFix,
  onLive,
  onOpen,
  onOpenTestCopy,
  onBackToEditing,
  onGone,
}: LaunchStageProps) {
  const published = (snapshot.lockedLifecycle ?? lifecycle) === "Published";
  const checking = snapshot.lockedLifecycle === "Checking";
  const [lookedForRunning, setLookedForRunning] = useState(!published);
  const [running, setRunning] = useState<string | null>(null);
  const [testCopy, setTestCopy] = useState<string | null>(checkingInstanceId);
  const [problems, setProblems] = useState<DefinitionProblem[] | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<LimitSheetView | null>(null);

  // A published design opens at Launch: find its running dial, if it still has one (its dial may have been archived since).
  useEffect(() => {
    if (!published) return;
    let cancelled = false;
    authoringCalls()
      .listMyDesigns()
      .then((designs) => {
        if (!cancelled) setRunning(runningDialOf(designs, id));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookedForRunning(true);
      });
    return () => {
      cancelled = true;
    };
  }, [published, id]);

  // Readiness read after a later save is newer than the problems going live reported.
  useEffect(() => {
    setProblems(null);
  }, [readiness]);

  const noun = segmentNoun(snapshot.noun ? { segmentNounSingular: snapshot.noun.singular, segmentNounPlural: snapshot.noun.plural } : null);
  const view = readinessView(problems ? withProblems(readiness, problems) : readiness, snapshot.model, noun);
  const lines = [...view.jumpable, ...view.general];

  const savedFirst = async () => {
    await session.flush();
    return !session.snapshot().unsaved;
  };

  async function tryItFirst() {
    setBusy("tryItFirst");
    setError(null);
    const result = await runTryItFirst({
      flush: savedFirst,
      freeze: () => session.freeze(),
      thaw: () => session.thaw(),
      send: () => authoringCalls().sendForChecking(id),
      lock: () => session.lock("Checking"),
      readTestCopy: async () => (await builderDeps().getDefinition(id)).checkingInstanceId ?? null,
    });
    setBusy(null);

    switch (result.kind) {
      case "testCopy":
        setTestCopy(result.instanceId);
        return;
      case "refused":
        setError(result.message || copy.launch.testCopyFailed);
        return;
      case "notSaved":
        setError(copy.frame.notSaved);
        return;
      case "trialEnded":
        // The 402 behind it has already switched the app to the trial-ended screen (KEY-637).
        return;
      case "gone":
        onGone();
        return;
      case "failed":
        setError(copy.launch.testCopyFailed);
        return;
    }
  }

  async function backToEditing() {
    setBusy("backToEditing");
    setError(null);
    const result = await runBackToEditing({ revert: () => authoringCalls().revert(id), unlock: () => session.unlock() });
    setBusy(null);

    switch (result.kind) {
      case "editing":
        setTestCopy(null);
        onBackToEditing();
        return;
      case "refused":
        setError(result.message || copy.launch.backToEditingFailed);
        return;
      case "gone":
        onGone();
        return;
      case "failed":
        setError(copy.launch.backToEditingFailed);
        return;
    }
  }

  async function goLive() {
    setBusy("goLive");
    setError(null);
    const result = await runGoLive({
      flush: savedFirst,
      freeze: () => session.freeze(),
      thaw: () => session.thaw(),
      goLive: () => authoringCalls().goLive(id),
    });
    setBusy(null);

    switch (result.kind) {
      case "live":
        onLive(result.instanceId);
        return;
      case "running":
        onOpen(result.instanceId);
        return;
      case "notReady":
        setProblems(result.problems);
        return;
      case "limit": {
        const allowance = await builderDeps()
          .getAllowance()
          .catch(() => null);
        setSheet(goLiveSheet(result.message, allowance));
        return;
      }
      case "refused":
        setError(result.message);
        return;
      case "notSaved":
        setError(copy.frame.notSaved);
        return;
      case "trialEnded":
        // The 402 behind it has already switched the app to the trial-ended screen (KEY-637).
        return;
      case "gone":
        onGone();
        return;
      case "failed":
        setError(copy.launch.failed);
        return;
    }
  }

  if (!lookedForRunning) {
    return <p className="kd-builder-muted">{copy.frame.loading}</p>;
  }

  if (running) {
    return (
      <section className="kd-builder-panel" aria-labelledby="kd-builder-question">
        <h1 id="kd-builder-question" className="kd-builder-question">
          {copy.launch.isLive}
        </h1>
        <div className="kd-builder-footer">
          <button type="button" className="kd-btn-primary" onClick={() => onOpen(running)}>
            {copy.launch.openDial}
          </button>
        </div>
      </section>
    );
  }

  const working = busy !== null;

  return (
    <section className="kd-builder-panel" aria-labelledby="kd-builder-question">
      <h1 id="kd-builder-question" className="kd-builder-question">
        {copy.launch.question}
      </h1>

      <h2 className="kd-builder-subheading">{copy.launch.checklistHeading}</h2>
      {view.ready ? (
        <p className="kd-launch-clear">{copy.launch.allClear}</p>
      ) : (
        <ul className="kd-launch-checklist">
          {lines.map((line) => (
            <li key={line.id}>
              <span>{line.sentence}</span>
              {line.jump && !checking && (
                <button
                  type="button"
                  className="kd-builder-link"
                  onClick={() => {
                    if (line.jump) onFix(line.jump);
                  }}
                >
                  {copy.launch.fixThis}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {checking ? (
        <div className="kd-launch-card kd-launch-test-copy">
          <h2 className="kd-choice-title">{copy.launch.testCopyBanner}</h2>
          <p className="kd-choice-body">{copy.launch.testCopyWarning}</p>
          <div className="kd-launch-actions">
            {testCopy && (
              <button type="button" className="kd-btn-primary" disabled={working} onClick={() => onOpenTestCopy(testCopy)}>
                {copy.launch.openTestCopy}
              </button>
            )}
            <button type="button" className="kd-btn" disabled={working} onClick={() => void backToEditing()}>
              {copy.launch.backToEditing}
            </button>
          </div>
        </div>
      ) : (
        <div className="kd-launch-card">
          <div className="kd-launch-card-head">
            <h2 className="kd-choice-title">{copy.launch.tryItFirst.title}</h2>
            <span className="kd-preset-chip">{copy.start.recommended}</span>
          </div>
          <p className="kd-choice-body">{copy.launch.tryItFirst.body}</p>
          <button type="button" className="kd-btn-primary" disabled={working || !view.ready} onClick={() => void tryItFirst()}>
            {busy === "tryItFirst" ? copy.launch.makingTestCopy : copy.launch.tryItFirst.title}
          </button>
        </div>
      )}

      <div className="kd-launch-card">
        <h2 className="kd-choice-title">{copy.launch.goLive.title}</h2>
        <p className="kd-choice-body">{copy.launch.goLive.body}</p>
        <button type="button" className={checking ? "kd-btn-primary" : "kd-btn"} disabled={working || !view.ready} onClick={() => void goLive()}>
          {busy === "goLive" ? copy.launch.goingLive : copy.launch.goLive.title}
        </button>
      </div>

      {error && (
        <p className="kd-builder-error" role="alert">
          {error}
        </p>
      )}
      {sheet && <LimitSheet view={sheet} onClose={() => setSheet(null)} />}
    </section>
  );
}
